import React, { useRef } from 'react';
import { Upload, FileText, X, AlertTriangle } from 'lucide-react';
import Button from '@components/common/Button';
import styles from './EvidenceUploadZone.module.css';

interface EvidenceUploadZoneProps {
  selectedFiles: File[];
  onFilesSelect: (files: File[]) => void;
  onFileRemove: (index: number) => void;
  onValidationError?: (error: string) => void;
  error?: string;
  isProcessing?: boolean;
}

const EvidenceUploadZone: React.FC<EvidenceUploadZoneProps> = ({
  selectedFiles,
  onFilesSelect,
  onFileRemove,
  onValidationError,
  error,
  isProcessing = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File validation constants
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['.pdf', '.doc', '.docx', '.xlsx', '.png', '.jpg', '.jpeg'];
  const acceptTypes = allowedTypes.join(',');

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    // Validate files
    const validFiles: File[] = [];
    const fileErrors: string[] = [];

    fileArray.forEach((file) => {
      // Check file size
      if (file.size > maxSize) {
        fileErrors.push(`${file.name} exceeds 10MB limit`);
        return;
      }

      // Check file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        fileErrors.push(`${file.name} has unsupported file type`);
        return;
      }

      validFiles.push(file);
    });

    if (fileErrors.length > 0 && onValidationError) {
      onValidationError(fileErrors.join(', '));
    }

    if (validFiles.length > 0) {
      onFilesSelect(validFiles);
    }
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className={styles.fileUploadArea}>
      <div className={styles.uploadZone}>
        <div className={styles.uploadIcon}>
          <Upload size={48} />
        </div>
        <div className={styles.uploadContent}>
          <h3>Drop files here or click to browse</h3>
          <p>Upload documents, screenshots, or other evidence files</p>
          <div className={styles.fileTypes}>
            <span>Supported formats: PDF, DOC, DOCX, XLSX, PNG, JPG, JPEG (Max 10MB each)</span>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className={styles.fileInputHidden}
          accept={acceptTypes}
          multiple
          onChange={handleFileChange}
          disabled={isProcessing}
          data-test-id="file-upload-input"
        />
        <Button
          variant="secondary"
          className={styles.browseButton}
          onClick={() => fileInputRef.current?.click()}
          type="button"
          disabled={isProcessing}
        >
          <FileText size={16} />
          Choose Files
        </Button>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className={styles.selectedFilesList} data-test-id="selected-files-list">
          <h4>Selected Files ({selectedFiles.length})</h4>
          {selectedFiles.map((file, index) => (
            <div key={`${file.name}-${file.size}`} className={styles.fileItem} data-test-id="file-item">
              <FileText size={16} className={styles.fileIcon} />
              <div className={styles.fileDetails}>
                <div className={styles.fileName}>{file.name}</div>
                <div className={styles.fileMeta}>
                  {formatFileSize(file.size)} • {file.type || 'Unknown type'}
                </div>
              </div>
              <button
                type="button"
                className={styles.removeFileButton}
                onClick={() => onFileRemove(index)}
                title="Remove file"
                disabled={isProcessing}
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className={styles.formError} data-test-id="file-error">
          <AlertTriangle size={14} />
          {error}
        </div>
      )}
    </div>
  );
};

export default EvidenceUploadZone;
